import { COMMENT_BODY_MAX } from '@mogzo/shared';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Avatar, CommunityIcon } from '../../components/community/Avatar';
import { GgButton } from '../../components/community/GgButton';
import {
  AuthorName,
  actionClass,
  BuildSplash,
  CommentIcon,
  commentsLabel,
  RoleBadge,
  ShareButton,
  TagChip,
} from '../../components/community/PostCard';
import { PostImages } from '../../components/community/PostImages';
import { CommunityShell, NoticeCard } from '../../components/community/Shell';
import { useLauncher } from '../../components/play/launch';
import { Alert, PageSpinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import {
  type Comment,
  createComment,
  fetchThread,
  type PostThread,
  removeComment,
  removePost,
  setPinned,
  timeAgo,
} from '../../lib/communities';
import { useSession } from '../../lib/session';
import { chipClass, PrivateTest } from './TestCommunity';

type State =
  | { status: 'loading' }
  | { status: 'private' }
  | { status: 'failed' }
  | { status: 'ready'; thread: PostThread };

/** mogzo.com/test/<address>/posts/<id> (or /team/…): one post and its comments. */
export function PostPage({ kind = 'test' }: { kind?: 'test' | 'team' | 'game' }) {
  const { slug = '', postId = '' } = useParams();
  const { me } = useSession();
  const [state, setState] = useState<State>({ status: 'loading' });

  const load = useCallback(async () => {
    try {
      const thread = await fetchThread(postId);
      setState({ status: 'ready', thread });
    } catch (error) {
      const hidden = error instanceof ApiError && [400, 401, 403, 404].includes(error.status);
      setState({ status: hidden ? 'private' : 'failed' });
    }
  }, [postId]);

  useEffect(() => {
    if (me === undefined) return;
    if (me === null) setState({ status: 'private' });
    else void load();
  }, [me, load]);

  if (state.status === 'loading') return <PageSpinner />;
  return (
    <CommunityShell title={state.status === 'ready' ? state.thread.community.name : undefined}>
      {state.status === 'private' &&
        (kind === 'game' ? (
          <NoticeCard title="Page not found">
            <p>This post was removed, or the address is wrong.</p>
          </NoticeCard>
        ) : (
          <PrivateTest kind={kind} next={`/${kind}/${slug}/posts/${postId}`} />
        ))}
      {state.status === 'failed' && (
        <NoticeCard title="Couldn’t load this post">
          <p>MOGZO didn’t answer. Reload the page to try again.</p>
        </NoticeCard>
      )}
      {state.status === 'ready' && <Thread thread={state.thread} reload={load} />}
    </CommunityShell>
  );
}

function Thread({ thread, reload }: { thread: PostThread; reload: () => Promise<void> }) {
  const { post, community } = thread;
  const navigate = useNavigate();
  const { launch, layer } = useLauncher({
    slug: community.slug,
    name: community.name,
    projectId: community.projectId ?? '',
    isDeveloper: post.canPin,
  });
  const [pinned, setPinnedState] = useState(post.pinned);
  const [error, setError] = useState<string | null>(null);
  const home = community.path;
  const playUrl = post.kind === 'build' ? post.build?.playUrl : null;

  async function togglePin() {
    setError(null);
    try {
      const saved = await setPinned(post.id, !pinned);
      setPinnedState(saved.pinned);
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t change the pin.');
    }
  }

  async function remove() {
    const whose = post.author.isYou ? 'your post' : `${post.author.name}’s post`;
    if (!window.confirm(`Remove ${whose}? Members won’t see it any more.`)) return;
    try {
      await removePost(post.id);
      navigate(home, { replace: true });
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t remove the post.');
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-[720px] flex-col gap-3.5 px-3 pt-5 pb-16 sm:px-6">
      <Link
        to={home}
        className="flex items-center gap-3 rounded-2xl border border-line bg-card px-3.5 py-2.5"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 6l-6 6 6 6" />
        </svg>
        <CommunityIcon name={community.name} size="sm" />
        <span>
          <strong>{community.name}</strong>{' '}
          <span className="text-sm text-muted">
            ·{' '}
            {community.kind === 'team'
              ? 'private team'
              : community.kind === 'game'
                ? 'game community'
                : 'private test'}
          </span>
        </span>
      </Link>

      <article className="overflow-hidden rounded-[18px] border border-line bg-card">
        <div className="flex items-center gap-2.5 px-5 pt-[18px]">
          <Avatar name={post.author.name} size="lg" />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex flex-wrap items-center gap-1.5">
              <AuthorName author={post.author} team={community.kind === 'team'} />
            </div>
            <span className="text-[13px] text-muted">
              {timeAgo(post.createdAt)}
              {post.kind === 'text' && post.build && ` · on build #${post.build.number}`}
            </span>
          </div>
          {pinned && <span className="text-xs font-bold text-muted">Pinned</span>}
          {post.tag && <TagChip tag={post.tag} />}
        </div>
        <div className="px-5 pt-3">
          {post.title && <h1 className="mb-1.5 text-2xl leading-tight font-bold">{post.title}</h1>}
          {post.body && (
            <p className="leading-relaxed whitespace-pre-line text-plum">{post.body}</p>
          )}
        </div>
        {post.images.length > 0 && (
          <div className="mx-5 mt-3">
            <PostImages images={post.images} author={post.author.name} />
          </div>
        )}
        {post.build && playUrl && community.projectId && community.developerName && (
          <div className="mx-5 mt-4">
            <BuildSplash
              build={{ ...post.build, playUrl }}
              community={{
                name: community.name,
                projectId: community.projectId,
                developerName: community.developerName,
              }}
              onLaunch={launch}
            />
            {layer}
          </div>
        )}
        {error && (
          <div className="mx-5 mt-3">
            <Alert>{error}</Alert>
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-divider px-3.5 py-3">
          <GgButton target="post" id={post.id} count={post.ggCount} on={post.viewerGg} />
          <span className={`${actionClass} hover:bg-transparent`}>
            <CommentIcon />
            {commentsLabel(thread.comments.filter((c) => c.body !== null).length)}
          </span>
          <ShareButton path={`${home}/posts/${post.id}`} />
          {(post.canPin || post.canRemove) && (
            <span className="ml-auto flex gap-1.5">
              {post.canPin && (
                <button
                  type="button"
                  onClick={togglePin}
                  className="min-h-9 rounded-full border border-line bg-card px-3.5 text-[13px] font-bold hover:border-muted"
                >
                  {pinned ? 'Unpin' : 'Pin'}
                </button>
              )}
              {post.canRemove && (
                <button
                  type="button"
                  onClick={remove}
                  className="min-h-9 rounded-full border border-[#f1c7c3] bg-[#fdf1f0] px-3.5 text-[13px] font-bold text-[#8a1c13] hover:bg-[#fbe4e2]"
                >
                  Remove
                </button>
              )}
            </span>
          )}
        </div>
      </article>

      <Comments thread={thread} reload={reload} />
    </main>
  );
}

const INDENT = ['', 'pl-6 sm:pl-[42px]', 'pl-12 sm:pl-[84px]'];

function CommentForm({
  postId,
  parent,
  onDone,
}: {
  postId: string;
  parent?: { id: string; author: string };
  onDone: () => Promise<void>;
}) {
  const { me } = useSession();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = parent ? `reply-${parent.id}` : 'comment';
  const label = parent ? `Reply to ${parent.author}` : 'Add a comment';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createComment(postId, body.trim(), parent?.id);
      setBody('');
      await onDone();
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t post. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={parent ? 'my-1.5' : ''}>
      {error && (
        <div className="mb-2">
          <Alert>{error}</Alert>
        </div>
      )}
      <div className="flex items-start gap-2.5">
        {!parent && me && <Avatar name={me.displayName} size="md" />}
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <input
          id={id}
          type="text"
          maxLength={COMMENT_BODY_MAX}
          placeholder={`${label}…`}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          // biome-ignore lint/a11y/noAutofocus: the reply box opens because Reply was pressed
          autoFocus={Boolean(parent)}
          className="min-h-10 min-w-0 flex-1 rounded-full border border-line bg-field px-3.5 text-ink placeholder:text-subtle focus:border-ink focus:bg-card focus:outline-none"
        />
        <button
          type="submit"
          disabled={busy || !body.trim()}
          className="min-h-10 rounded-full bg-ink px-[18px] text-sm font-bold text-white hover:bg-ink-soft disabled:opacity-50"
        >
          {parent ? 'Reply' : 'Post'}
        </button>
      </div>
    </form>
  );
}

function Comments({ thread, reload }: { thread: PostThread; reload: () => Promise<void> }) {
  const team = thread.community.kind === 'team';
  const [sort, setSort] = useState<'top' | 'new'>('top');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [replying, setReplying] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Depth-first: top-level comments by the chosen sort, replies oldest first. */
  const ordered = useMemo(() => {
    const children = new Map<string | null, Comment[]>();
    for (const c of thread.comments) {
      children.set(c.parentId, [...(children.get(c.parentId) ?? []), c]);
    }
    const roots = [...(children.get(null) ?? [])].sort((a, b) =>
      sort === 'new'
        ? b.createdAt.localeCompare(a.createdAt)
        : b.ggCount - a.ggCount || a.createdAt.localeCompare(b.createdAt),
    );
    const out: { comment: Comment; replies: number }[] = [];
    const walk = (c: Comment) => {
      const replies = children.get(c.id) ?? [];
      out.push({ comment: c, replies: replies.length });
      if (!collapsed.has(c.id)) replies.forEach(walk);
    };
    roots.forEach(walk);
    return out;
  }, [thread.comments, sort, collapsed]);

  function toggle(id: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function remove(comment: Comment) {
    if (!window.confirm('Remove this comment?')) return;
    setError(null);
    try {
      await removeComment(comment.id);
      await reload();
    } catch (failed) {
      setError(failed instanceof Error ? failed.message : 'Couldn’t remove the comment.');
    }
  }

  return (
    <section
      aria-labelledby="comments-heading"
      className="rounded-[18px] border border-line bg-card px-4 pt-4 pb-2 sm:px-5"
    >
      <h2 id="comments-heading" className="sr-only">
        Comments
      </h2>
      <CommentForm postId={thread.post.id} onDone={reload} />
      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}

      {thread.comments.length > 1 && (
        <fieldset className="mt-3.5 flex items-center gap-1.5 text-[13px]">
          <legend className="sr-only">Sort comments</legend>
          <span className="text-muted">Sort:</span>
          {(['top', 'new'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={sort === value}
              onClick={() => setSort(value)}
              className={chipClass(sort === value)}
            >
              {value === 'top' ? 'Top' : 'New'}
            </button>
          ))}
        </fieldset>
      )}

      {thread.comments.length === 0 && (
        <p className="py-6 text-center text-sm text-muted">No comments yet.</p>
      )}

      <ul className="mt-2">
        {ordered.map(({ comment, replies }) => {
          const open = !collapsed.has(comment.id);
          const author = comment.author;
          return (
            <li
              key={comment.id}
              className={`flex gap-2.5 pt-2.5 pb-0.5 ${INDENT[comment.depth] ?? INDENT[2]}`}
            >
              <div className="flex shrink-0 flex-col items-center">
                {author ? (
                  <Avatar name={author.name} size="sm" />
                ) : (
                  <span aria-hidden="true" className="size-8 rounded-full bg-field" />
                )}
                {replies > 0 && open && (
                  <span
                    aria-hidden="true"
                    className="mt-1 min-h-5 w-0.5 flex-1 rounded-[1px] bg-divider"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                  {author ? (
                    <>
                      <strong>{author.name}</strong>
                      <RoleBadge role={author.role} team={team} />
                    </>
                  ) : (
                    <span className="text-muted">Removed</span>
                  )}
                  <span className="text-[13px] text-muted">· {timeAgo(comment.createdAt)}</span>
                </div>
                <p
                  className={`mt-0.5 leading-normal whitespace-pre-line ${comment.body === null ? 'text-muted italic' : ''}`}
                >
                  {comment.body ?? 'This comment was removed.'}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-0.5">
                  {comment.body !== null && (
                    <>
                      <GgButton
                        target="comment"
                        id={comment.id}
                        count={comment.ggCount}
                        on={comment.viewerGg}
                        compact
                      />
                      <button
                        type="button"
                        aria-expanded={replying === comment.id}
                        onClick={() => setReplying(replying === comment.id ? null : comment.id)}
                        className="min-h-[34px] rounded-full px-2.5 text-[13px] font-bold text-muted hover:text-ink"
                      >
                        Reply
                      </button>
                    </>
                  )}
                  {replies > 0 && (
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggle(comment.id)}
                      className="min-h-[34px] rounded-full px-2.5 text-[13px] font-bold text-muted hover:text-ink"
                    >
                      {open ? 'Hide replies' : `Show replies (${replies})`}
                    </button>
                  )}
                  {comment.canRemove && (
                    <button
                      type="button"
                      onClick={() => remove(comment)}
                      className="min-h-[34px] rounded-full px-2.5 text-[13px] font-bold text-muted hover:text-[#8a1c13]"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {replying === comment.id && author && (
                  <CommentForm
                    postId={thread.post.id}
                    parent={{ id: comment.id, author: author.name }}
                    onDone={async () => {
                      setReplying(null);
                      await reload();
                    }}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
